/**
 * Confirmation before deducting 1 report credit to unlock a project.
 * Spec-verbatim copy.
 */
import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getLocale, useT } from "@/lib/i18n";
import { recordReportVersion } from "@/lib/reportVersion";
import { creditsLabel, unlockedToast } from "@/lib/purchaseModalToast";

const L = (en, fr) => (getLocale() === "fr" ? fr : en);

export const ConfirmUnlockModal = ({ open, onClose, project, walletBefore, onUnlocked }) => {
  useT();
  const [busy, setBusy] = useState(false);
  if (!open || !project) return null;
  const walletAfter = Math.max(0, (walletBefore || 0) - 1);
  const lang = getLocale();

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/unlock-with-credit`);
      // Stage 2: snapshot the report version so it lives permanently in the user's library
      await recordReportVersion(project.id, "credit");
      toast.success(unlockedToast(walletAfter, getLocale()));
      onUnlocked && onUnlocked();
      onClose();
    } catch (e) {
      if (e.response?.status === 402) toast.error(L("No credits — please buy a bundle first", "Aucun crédit — achetez d'abord une offre"));
      else toast.error(L("Unlock failed", "Échec du déblocage"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(22,33,29,0.55)" }} data-testid="confirm-unlock-modal">
      <div className="w-full max-w-md rounded-lg shadow-xl relative p-8" style={{ background: "var(--white)" }}>
        <button onClick={onClose} aria-label={L("Close", "Fermer")} className="absolute top-4 right-4" style={{ color: "var(--muted)" }} data-testid="confirm-unlock-close"><X size={18} /></button>
        <div className="overline mb-1">{L("Confirm unlock", "Confirmer le déblocage")}</div>
        <h3 className="font-display text-2xl mb-4">{L("Use 1 report credit?", "Utiliser 1\u00a0crédit de rapport\u00a0?")}</h3>
        <p className="text-sm mb-6" data-testid="confirm-unlock-text">
          {L("Unlock the detailed report for ", "Débloquer le rapport détaillé de ")}<b>{project.name || L("this project", "ce projet")}</b>.
          <br />
          {lang === "fr"
            ? <>Il restera ensuite <b>{creditsLabel(walletAfter, "fr")}</b> dans votre portefeuille.</>
            : <><b>{walletAfter}</b> report credit{walletAfter === 1 ? "" : "s"} will remain in your wallet after this.</>}
        </p>
        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="btn-secondary" disabled={busy} data-testid="confirm-unlock-cancel">{L("Cancel", "Annuler")}</button>
          <button onClick={confirm} className="btn-primary" disabled={busy} data-testid="confirm-unlock-do">
            {busy ? L("Unlocking…", "Déblocage…") : L("Unlock report", "Débloquer le rapport")}
          </button>
        </div>
      </div>
    </div>
  );
};
